'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import UILink from 'UIComponents/link/UILink';
import ThemeSearchBarResult from 'ecosystem-marketplace-core/assets/components/ThemeSearchBarResult';
import CoreSearchBar from 'ecosystem-marketplace-core/components/storefront/SearchBar';
import InputHighlighter from 'ecosystem-marketplace-core/components/storefront/InputHighlighter';
import MediaQuery from 'ecosystem-marketplace-core/components/layout/MediaQuery';
import { debounce } from 'ecosystem-marketplace-core/utils/debounce';
import { TYPEAHEAD_SEARCH_RESULTS_LIMIT, TYPEAHEAD_SEARCH_MIN_CHARS } from 'ecosystem-marketplace-core/constants/search';
import { buildGetCachedSearchValues, buildSetCachedSearchValue } from 'ecosystem-marketplace-core/utils/cachedSearch';
import { getAssetBusinessTypeI18nKey, getAssetPageTypeI18nKey, getAssetPageFeatureI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import * as AssetPageType from 'ecosystem-marketplace-core/constants/groupTags/AssetPageType';
import * as AssetPageFeature from 'ecosystem-marketplace-core/constants/groupTags/AssetPageFeature';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import * as EmbedFrame from 'marketplace-embed/constants/EmbedFrame';
import * as EmbedGroup from 'marketplace-embed/constants/EmbedGroup';
import * as PostMessageType from 'marketplace-embed/constants/PostMessageType';
import { searchThemes } from '../../../clients/ecosystemClient';
import * as ThemeQueryField from '../../../constants/ThemeQueryField';
import StorefrontUsageTrackerContext from '../../../context/StorefrontUsageTrackerContext';
import { useEmbedContext } from '../../../hooks/useEmbedContext';
import ThemeQuery from '../../../records/ThemeQuery';
import ThemeSearchResponse from '../../../records/ThemeSearchResponse';
import { getBusinessTypes } from '../../../selectors/themes';
import { getLocationFromThemeQuery } from '../../../utils/themeQuery';
import MobileSearchBar from './MobileSearchBar';
const CACHED_SEARCH_VALUES_KEY = 'marketplace-assets:searches';
const getCachedSearchValues = buildGetCachedSearchValues(CACHED_SEARCH_VALUES_KEY);
const setCachedSearchValue = buildSetCachedSearchValue(CACHED_SEARCH_VALUES_KEY);
const EMPTY_THEME_SEARCH_RESPONSE = new ThemeSearchResponse();
const THEME_QUERY_FIELD_ORDER = [ThemeQueryField.BUSINESS_TYPES, ThemeQueryField.PAGE_TYPES, ThemeQueryField.PAGE_FEATURES];

const getSearchPlaceholderKey = () => {
  // TODO generalize for other asset types
  return 'marketplace-assets-ui.storefront.header.search.placeholders.themes';
};

const getFullFilterGroup = (filterTypes, getTranslationKey, filterKey, message) => {
  const options = filterTypes.map(filterType => {
    const translatedText = I18n.text(getTranslationKey(filterType));
    return {
      dropdownText: /*#__PURE__*/_jsx(InputHighlighter, {
        text: translatedText
      }),
      filterKey,
      text: translatedText,
      value: filterType
    };
  });
  return {
    text: I18n.text(message),
    options
  };
};

const getDisplayedFilterGroup = (renderedOptions, input) => {
  const filteredOptions = renderedOptions.options.filter(option => option.text.toLowerCase().includes(input));
  return Object.assign({}, renderedOptions, {
    options: filteredOptions
  });
};

const renderDropdownAssetPreviewText = ({
  thumbnailUrl,
  name,
  companyName
}) => {
  return /*#__PURE__*/_jsx(ThemeSearchBarResult, {
    companyName: companyName,
    name: name,
    thumbnailUrl: thumbnailUrl
  });
};

const getAssetOptions = ({
  items,
  total
}, input, sectionTitle) => {
  const assetOptions = [];
  items.forEach(record => assetOptions.push({
    value: `${record.offeringId}`,
    text: record.name,
    slug: record.slug,
    title: record.name,
    dropdownText: renderDropdownAssetPreviewText(record)
  }));

  if (total > TYPEAHEAD_SEARCH_RESULTS_LIMIT) {
    assetOptions.push({
      value: input,
      viewAll: true,
      dropdownText: /*#__PURE__*/_jsx(UILink, {
        className: "m-left-5",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.storefront.header.search.seeAll",
          options: {
            resultsCount: total
          }
        })
      })
    });
  }

  return {
    text: sectionTitle,
    options: assetOptions
  };
};

const StorefrontSearchBar = ({
  isSticky
}) => {
  const {
    isEmbedded,
    name: embedFrameName,
    sendMessage,
    sendBroadcastMessage
  } = useEmbedContext();
  const history = useHistory();
  const location = useLocation();
  const {
    search
  } = location;
  const cachedSearchValues = getCachedSearchValues();
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const businessTypes = useSelector(getBusinessTypes);
  const filterGroups = useMemo(() => {
    // TODO generalize for other asset types
    return {
      [ThemeQueryField.BUSINESS_TYPES]: getFullFilterGroup(businessTypes, getAssetBusinessTypeI18nKey, ThemeQueryField.BUSINESS_TYPES, 'marketplace-assets-ui.themes.filters.businessType.title'),
      [ThemeQueryField.PAGE_TYPES]: getFullFilterGroup(Object.values(AssetPageType), getAssetPageTypeI18nKey, ThemeQueryField.PAGE_TYPES, 'marketplace-assets-ui.themes.filters.pageTypes.title'),
      [ThemeQueryField.PAGE_FEATURES]: getFullFilterGroup(Object.values(AssetPageFeature), getAssetPageFeatureI18nKey, ThemeQueryField.PAGE_FEATURES, 'marketplace-assets-ui.themes.filters.pageTypes.title')
    };
  }, [businessTypes]);
  const computedQuery = useMemo(() => {
    // TODO generalize for other asset types
    return ThemeQuery.fromLocation({
      search
    });
  }, [search]);
  const searchTerm = useMemo(() => {
    return computedQuery.get(ThemeQueryField.SEARCH) || '';
  }, [computedQuery]);
  const searchInputSize = useMemo(() => {
    if (!isSticky) {
      return 'xl';
    }

    return 'default';
  }, [isSticky]);
  const recentSearchOptions = useMemo(() => {
    if (!cachedSearchValues.length) {
      return [];
    }

    return [{
      text: I18n.text(`marketplace-assets-ui.storefront.header.search.recent`),
      options: cachedSearchValues.map(value => ({
        text: value,
        value,
        cachedSearchValue: true
      }))
    }];
  }, [cachedSearchValues]);
  const handleThemeQueryChange = useCallback(themeQuery => {
    const newLocation = getLocationFromThemeQuery(themeQuery, location);
    history.push(newLocation);
  }, [history, location]);
  const formatDropdownOptions = useCallback((recordData, input) => {
    const formattedOptions = [];

    if (recordData.themeSearchResponse) {
      formattedOptions.push(getAssetOptions(recordData.themeSearchResponse, input, I18n.text('marketplace-assets-ui.storefront.header.search.sectionTitles.themes')));
    }

    THEME_QUERY_FIELD_ORDER.forEach(filterGroupKey => formattedOptions.push(getDisplayedFilterGroup(filterGroups[filterGroupKey], input)));
    return formattedOptions;
  }, [filterGroups]);
  const loadOptions = useCallback((input, callback) => {
    if (input.trim().length < TYPEAHEAD_SEARCH_MIN_CHARS) {
      callback(null, {
        options: recentSearchOptions
      });
      return;
    } // TODO generalize for other asset types


    const formatOptions = (resp, searchInput) => formatDropdownOptions({
      themeSearchResponse: resp
    }, searchInput);

    searchThemes([{
      search: input,
      limit: TYPEAHEAD_SEARCH_RESULTS_LIMIT
    }]).then(([resp]) => {
      const options = formatOptions(new ThemeSearchResponse(resp), input);
      callback(null, {
        options
      });
    }).catch(() => {
      const options = formatOptions(EMPTY_THEME_SEARCH_RESPONSE, input);
      callback(null, {
        options
      });
    });
  }, [recentSearchOptions, formatDropdownOptions]);
  const debouncedLoadOptions = useMemo(() => debounce(loadOptions), [loadOptions]);
  const onSearchSubmit = useCallback(value => {
    const valuesFromCache = getCachedSearchValues();
    setCachedSearchValue(value, valuesFromCache); // TODO generalize for other asset types

    handleThemeQueryChange(computedQuery.set(ThemeQueryField.SEARCH, value));
    trackInteraction('submit search', {
      search: value,
      searchLength: value.length
    });
  }, [computedQuery, handleThemeQueryChange, trackInteraction]);
  const trackSelectedOptionInteraction = useCallback((itemType, value) => {
    trackInteraction('select search autofill item', {
      itemType,
      value
    });
  }, [trackInteraction]);
  const onSelectedOptionChange = useCallback(option => {
    const {
      value,
      filterKey,
      cachedSearchValue,
      slug,
      viewAll
    } = option;

    if (viewAll) {
      onSearchSubmit(value);
      trackSelectedOptionInteraction('see all results', value);
      return;
    }

    if (filterKey) {
      // TODO generalize for other asset types
      const newQueryValue = computedQuery.get(filterKey).add(value);
      handleThemeQueryChange(computedQuery.set(filterKey, newQueryValue));
      trackSelectedOptionInteraction(filterKey, value);
      return;
    }

    if (cachedSearchValue) {
      onSearchSubmit(value);
      trackSelectedOptionInteraction('recent search', value);
      return;
    }

    if (isEmbedded && embedFrameName === EmbedFrame.CONTENT_CREATOR_STOREFRONT) {
      const messageArgs = [PostMessageType.NAVIGATE_TO_THEME_DETAILS, {
        slug
      }];
      sendBroadcastMessage(...messageArgs, EmbedGroup.CONTENT_CREATOR);
      sendMessage(...messageArgs);
      return;
    } // TODO generalize for other asset types


    history.push(`/themes/${slug}`);
    trackSelectedOptionInteraction('offering', slug);
  }, [computedQuery, embedFrameName, handleThemeQueryChange, history, isEmbedded, onSearchSubmit, sendBroadcastMessage, sendMessage, trackSelectedOptionInteraction]);
  const onClearSearch = useCallback(() => {
    // TODO generalize for other asset types
    handleThemeQueryChange(computedQuery.delete(ThemeQueryField.SEARCH));
  }, [computedQuery, handleThemeQueryChange]);
  return /*#__PURE__*/_jsx(MediaQuery, {
    alternate: /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(MobileSearchBar, {
        loadOptions: debouncedLoadOptions,
        onClearSearch: onClearSearch,
        onSearchSubmit: onSearchSubmit,
        onSelectedOptionChange: onSelectedOptionChange,
        placeholder: unescapedText(getSearchPlaceholderKey()),
        searchTerm: searchTerm
      })
    }),
    minScreenSize: ScreenSizes.SM,
    children: /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(CoreSearchBar, {
        loadOptions: debouncedLoadOptions,
        onSearchSubmit: onSearchSubmit,
        onSelectedOptionChange: event => onSelectedOptionChange(event.target.value),
        inputSize: searchInputSize,
        inputValue: searchTerm,
        onClear: onClearSearch,
        placeholder: unescapedText(getSearchPlaceholderKey())
      })
    })
  });
};

export default StorefrontSearchBar;